interface NurseryRef {
  id: string;
}

interface ResolveSelectedNurseryIdArgs {
  explicitId: string | null;
  cachedId: string | null;
  nurseryAccessDefaultId: string | null;
  nurseries: NurseryRef[];
}

/**
 * Resolve which nursery should be selected, validating every candidate
 * against the loaded nursery list so a stale id can never stick (NAU-335).
 * Precedence: explicit selection > cached selection > nursery access default
 * > first nursery. While the list is empty (still loading, or offline with an
 * empty cache) candidates pass through unvalidated so flows keyed on the id
 * are not blocked.
 */
export function resolveSelectedNurseryId({
  explicitId,
  cachedId,
  nurseryAccessDefaultId,
  nurseries,
}: ResolveSelectedNurseryIdArgs): string | null {
  if (nurseries.length === 0) {
    return explicitId ?? cachedId ?? nurseryAccessDefaultId ?? null;
  }

  const isInList = (id: string | null): id is string =>
    id !== null && nurseries.some((nursery) => nursery.id === id);

  if (isInList(explicitId)) {
    return explicitId;
  }
  if (isInList(cachedId)) {
    return cachedId;
  }
  if (isInList(nurseryAccessDefaultId)) {
    return nurseryAccessDefaultId;
  }
  return nurseries[0]?.id ?? null;
}
